import React, { useState } from 'react';

const API = process.env.REACT_APP_SERVER_URL || '';
const MAX_DESC = 1000;

const CATEGORIES = [
  { value: 'harassment',         label: 'Harassment / hate speech' },
  { value: 'spam',               label: 'Spam / advertising' },
  { value: 'cheating',           label: 'Cheating / exploiting' },
  { value: 'inappropriate_name', label: 'Inappropriate name' },
  { value: 'scam',               label: 'Scam / fraud' },
  { value: 'other',              label: 'Other' },
];

/**
 * ReportPlayerModal — submits a player report to /api/reports.
 *
 * Props:
 *   target               — { id, name } of the reported player
 *   recentChatMessages   — optional array of recent messages for evidence snapshot
 *   onClose()            — dismiss the modal
 *   onSubmitted(result)  — optional callback on successful submit
 */
const ReportPlayerModal = ({ target, recentChatMessages = [], onClose, onSubmitted }) => {
  const [category, setCategory] = useState('harassment');
  const [description, setDescription] = useState('');
  const [includeChat, setIncludeChat] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!target) return null;

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        reported_character_id: target.id,
        reported_name: target.name,
        category,
        description: description.slice(0, MAX_DESC),
      };
      if (includeChat && recentChatMessages.length) {
        // keep only last 20, strip to minimal fields to limit payload
        body.evidence = {
          chat: recentChatMessages.slice(-20).map(m => ({
            sender: m.senderName || m.sender || m.senderName,
            channel: m.channel || null,
            text: (m.message || m.text || '').slice(0, 300),
            ts: m.timestamp || m.createdAt || null,
          })),
        };
      }
      const res = await fetch(`${API}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 429) {
          setError(data.error || 'Please wait before sending another report.');
        } else if (res.status === 400) {
          setError(data.error || 'Invalid report.');
        } else {
          setError(data.error || `Server error (${res.status})`);
        }
        return;
      }
      setDone(true);
      onSubmitted?.(data);
    } catch (e) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10001,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 460, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto',
          background: '#1a1a2e', border: '1px solid #4a4a6a',
          borderRadius: 8, padding: 20, color: '#e0e0e0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#ffd777' }}>🚩 Report player</h3>
          <button onClick={onClose} style={btnCloseStyle}>✕</button>
        </div>

        <div style={{ marginBottom: 10, fontSize: 13, color: '#bbb' }}>
          Reporting <b style={{ color: '#ffd777' }}>{target.name}</b>
        </div>

        {done ? (
          <div style={{ padding: '16px 0' }}>
            <div style={{ color: '#88ff88', marginBottom: 12 }}>
              ✅ Report submitted. A moderator will review it.
            </div>
            <button onClick={onClose} style={btnPrimaryStyle}>Close</button>
          </div>
        ) : (
          <>
            <label style={labelStyle}>Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <label style={labelStyle}>
              What happened? <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value.slice(0, MAX_DESC))}
              rows={5}
              placeholder="Briefly describe the issue. Do not include personal info."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              disabled={submitting}
            />
            <div style={{ fontSize: 11, color: '#888', textAlign: 'right', marginTop: -6, marginBottom: 10 }}>
              {description.length} / {MAX_DESC}
            </div>

            {recentChatMessages.length > 0 && (
              <label style={{ ...labelStyle, fontWeight: 'normal', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={includeChat}
                  onChange={e => setIncludeChat(e.target.checked)}
                  disabled={submitting}
                />
                Include recent chat ({Math.min(recentChatMessages.length, 20)} messages) as evidence
              </label>
            )}

            {error && (
              <div style={{
                padding: '8px 10px', marginTop: 8, marginBottom: 8,
                background: '#3a1a1a', border: '1px solid #7a3a3a', borderRadius: 4,
                color: '#ffaaaa', fontSize: 12,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={btnSecondaryStyle} disabled={submitting}>
                Cancel
              </button>
              <button onClick={submit} style={btnPrimaryStyle} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>

            <div style={{ marginTop: 14, fontSize: 11, color: '#888', lineHeight: 1.5 }}>
              False reports may themselves be actioned. Reports are private —
              the reported player is not notified.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block', marginTop: 10, marginBottom: 4,
  fontSize: 12, color: '#bbb', fontWeight: 600,
};
const inputStyle = {
  width: '100%', padding: '8px 10px',
  background: '#0f0f1e', border: '1px solid #4a4a6a', borderRadius: 4,
  color: '#e0e0e0', fontSize: 13, boxSizing: 'border-box',
};
const btnPrimaryStyle = {
  padding: '8px 16px', background: '#c44', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
const btnSecondaryStyle = {
  padding: '8px 16px', background: '#333', color: '#ddd',
  border: '1px solid #555', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const btnCloseStyle = {
  background: 'transparent', border: 'none', color: '#888',
  fontSize: 18, cursor: 'pointer', padding: 4,
};

export default ReportPlayerModal;
